import React from 'react';
import {useTranslation} from 'react-i18next';
import {EAutocompleteOption} from 'webapp-lib/Components';

import casusBackend, {CasusSearchItem} from '@lexian/api/casus-api';
import {assignableCasusFilters} from '_/store/searchCasus';
import {casusStateItems, CasusStateTranslation} from '_/utilities/casus.utils';
import {requiredRule} from '_/utilities/form';
import {EFCheckboxGroup} from '_/utilities/form/controlled-components';
import {PageItem, PaginationResponse} from '_/utilities/types';

import {
  AutocompleteBase,
  AutocompleteFilter,
  paginatedLoader,
} from '../AutocompleteBase/AutocompleteBase';

import {
  CasusSearchFilterColumn,
  CasusSearchFilterRow,
} from './CasusAutocomplete.styles';
import {
  CasusAutocompleteProps,
  CasusStateFilter,
  MinimalCasusSearchItem,
} from './CasusAutocomplete.types';
import {
  CASUS_CATEGORIES,
  mapStateFilter,
  mapToOption,
} from './CasusAutocomplete.utils';

type CasusFilterState = {casusState: CasusStateFilter[]};

const DEFAULT_CASUS_STATES: CasusStateFilter[] = ['ACTIVE'];

export function CasusAutocomplete<
  T extends MinimalCasusSearchItem = CasusSearchItem,
>({
  filters = assignableCasusFilters,
  searchFiltersPersistenceKey,
  onSelectedCasusIsFilteredOut,
  disableUnassignableCasus = false,
  onCasusChange,
  recommendedOptions,
  categories = CASUS_CATEGORIES,
  ...rest
}: CasusAutocompleteProps<T>): React.ReactElement {
  const {t} = useTranslation('common');
  const searchFilter = useCasusSearchFilter({
    persistenceKey: searchFiltersPersistenceKey,
  });

  return (
    <AutocompleteBase
      {...rest}
      additionalItems={recommendedOptions}
      categories={categories}
      filter={searchFilter}
      labelInfo={
        disableUnassignableCasus
          ? t('casusAutocomplete.optionDisabledReason')
          : undefined
      }
      loadItems={paginatedLoader({
        endpointFunction: casusBackend.searchAllCasus,
        // The cast is necessary because the server call cannot be made
        // generic on the type T.
        getPageItemFromResponse: (body) =>
          body.casus as PaginationResponse<
            PageItem<MinimalCasusSearchItem>
          > as PaginationResponse<PageItem<T>>,
        params: (filterState: CasusFilterState | undefined) => ({
          ...filters,
          stateFilters: filterState?.casusState ?? DEFAULT_CASUS_STATES,
        }),
      })}
      mapOptions={(items, context) =>
        mapCasusOptions({
          items,
          recommendedOptions: recommendedOptions ?? [],
          disableUnassignableCasus,
          ...context,
        })
      }
      reloadTrigger={filters}
      toOption={(casus) => mapToOption(casus, disableUnassignableCasus)}
      noOptionFiltering
      onSelectedItemChange={onCasusChange}
      onSelectedItemMissing={onSelectedCasusIsFilteredOut}
    />
  );
}

function useCasusSearchFilter({
  persistenceKey,
}: {
  persistenceKey: string;
}): AutocompleteFilter<CasusFilterState> {
  const {t} = useTranslation('common');
  return {
    defaultState: {casusState: DEFAULT_CASUS_STATES},
    dialogHeader: t('casusAutocomplete.searchFilters.dialog.header'),
    persistenceKey: `casusAutocomplete.searchFilters.${persistenceKey}`,
    toText: ({casusState}) =>
      casusState.map((state) => CasusStateTranslation[state]).join(', '),
    warningText: t('casusAutocomplete.filteredOutWarningText'),
    renderFields: (control) => (
      <>
        <CasusSearchFilterRow>
          <CasusSearchFilterColumn>
            <EFCheckboxGroup
              control={control}
              items={casusStateItems}
              label={t(
                'casusAutocomplete.searchFilters.dialog.casusStateFilter'
              )}
              name='casusState'
              rules={requiredRule}
            />
          </CasusSearchFilterColumn>
        </CasusSearchFilterRow>
      </>
    ),
  };
}

function mapCasusOptions<T extends MinimalCasusSearchItem>({
  items,
  recommendedOptions,
  disableUnassignableCasus,
  searchQuery,
  hasMoreBefore,
  filterState,
}: {
  items: T[];
  recommendedOptions: T[];
  disableUnassignableCasus: boolean;
  searchQuery: string;
  hasMoreBefore: boolean;
  filterState: CasusFilterState | undefined;
}): EAutocompleteOption[] {
  const stateFilters = filterState?.casusState ?? DEFAULT_CASUS_STATES;
  const filteredRecommendedOptions = recommendedOptions.filter(
    (it) =>
      it.displayNameWithClient
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) &&
      it.active === mapStateFilter(stateFilters)
  );

  const filteredItems = filterOutRecommendedOptions(
    items,
    filteredRecommendedOptions
  );

  return [
    ...(hasMoreBefore ? [] : filteredRecommendedOptions),
    ...filteredItems,
  ].map((it) => mapToOption(it, disableUnassignableCasus));
}

function filterOutRecommendedOptions<T extends MinimalCasusSearchItem>(
  items: T[],
  recommendedOptions: T[]
): T[] {
  if (recommendedOptions.length === 0) {
    return items;
  }
  const recommendedIds = recommendedOptions.map((it) => it.id);
  return items.filter((item) => !recommendedIds.includes(item.id));
}
